import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import Keycloak from 'keycloak-js';

interface AuthContextType {
  keycloak: Keycloak | null;
  authenticated: boolean;
  loading: boolean;
  token: string | undefined;
  user: {
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    roles?: string[];
  } | null;
  login: () => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  keycloakConfig: {
    url: string;
    realm: string;
    clientId: string;
  };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, keycloakConfig }) => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthContextType['user']>(null);
  
  // Ref to track initialization and prevent double init in StrictMode
  const initializingRef = useRef(false);
  const keycloakRef = useRef<Keycloak | null>(null);

  useEffect(() => {
    const initKeycloak = async () => {
      // Prevent double initialization (React StrictMode runs effects twice)
      if (initializingRef.current || keycloakRef.current) {
        console.log('Keycloak already initializing or initialized, skipping...');
        return;
      }
      initializingRef.current = true;

      try {
        // Check for login_required error in URL hash (from silent SSO check failure)
        // This prevents infinite redirect loop when silent check fails
        const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
        const errorParam = hashParams.get('error');
        const hasLoginRequiredError = errorParam === 'login_required';
        
        // Check if we were redirected back after a login attempt
        const loginAttempted = sessionStorage.getItem('keycloak_login_attempted') === 'true';

        // Check if we're landing here after an explicit logout — skip check-sso in that case
        // (user is definitely unauthenticated, no need to run silent SSO + 3p-cookie checks)
        const isPostLogout = sessionStorage.getItem('keycloak_post_logout') === 'true';
        if (isPostLogout) {
          sessionStorage.removeItem('keycloak_post_logout');
        }
        
        if (hasLoginRequiredError) {
          console.log('Detected login_required error - clearing URL and will show login prompt');
          // Store flag in sessionStorage to prevent immediate auto-login
          sessionStorage.setItem('keycloak_login_required', 'true');
          sessionStorage.removeItem('keycloak_login_attempted');
        }
        
        // Only clean up error hash parameters, preserve search params (like workflowId)
        if (window.location.hash.includes('error') && !window.location.search.includes('code=')) {
          const cleanUrl = window.location.origin + window.location.pathname + window.location.search;
          window.history.replaceState({}, document.title, cleanUrl);
        }

        // Save current app query params BEFORE any Keycloak redirect might happen
        // This is critical for page refresh scenarios where check-sso might redirect
        const currentUrl = new URL(window.location.href);
        const currentParams = new URLSearchParams(currentUrl.search);
        
        // Remove Keycloak auth params, keep only app params
        const keycloakAuthParams = ['state', 'session_state', 'code', 'error', 'error_description'];
        keycloakAuthParams.forEach(param => currentParams.delete(param));
        
        // Save app params (like workflowId) if present and not already saved
        const appParams = currentParams.toString();
        if (appParams && !sessionStorage.getItem('keycloak_redirect_params')) {
          console.log('Saving app params before Keycloak init:', appParams);
          sessionStorage.setItem('keycloak_redirect_params', appParams);
        }

        console.log('Initializing Keycloak with URL params:', window.location.search);

        const keycloakInstance = new Keycloak({
          url: keycloakConfig.url,
          realm: keycloakConfig.realm,
          clientId: keycloakConfig.clientId,
        });

        // Store in ref immediately to prevent double creation
        keycloakRef.current = keycloakInstance;

        // Check if we're returning from a login callback (has code param)
        const urlParams = new URLSearchParams(window.location.search);
        const hasAuthCallback = urlParams.has('code') && urlParams.has('state');

        // Detect if we're on a tunnel/dev environment where silent SSO iframe won't work
        const origin = window.location.origin;
        const isDevEnvironment = origin.includes('localhost') || 
                                  origin.includes('127.0.0.1') ||
                                  origin.includes('ngrok') ||
                                  origin.includes('.local');
        
        console.log('Keycloak init options:', { hasAuthCallback, isDevEnvironment, origin });

        // Only use silent SSO redirect in production environments
        // Dev/tunnel environments have iframe/cookie issues that cause timeouts
        // In dev, don't use check-sso as it can cause redirect loops
        const initOptions: Keycloak.KeycloakInitOptions = {
          // Don't auto-check SSO - let ProtectedRoute handle login redirect
          // This prevents redirect loops when SSO check fails
          onLoad: hasAuthCallback ? undefined : undefined, // Never auto-redirect
          checkLoginIframe: false,
          pkceMethod: 'S256',
          flow: 'standard',
          // Limit 3p-cookie iframe check to 1.5s — default is 10s and the iframe is
          // blocked by browser CSP/response headers, causing a full 10s hang on every
          // page load/refresh in production.
          messageReceiveTimeout: 1500,
        };

        // Enable silent SSO only in production, only if not in a callback/post-logout state
        // Post-logout: user just logged out, skip check-sso (they are definitely unauthenticated)
        if (!isDevEnvironment && !hasAuthCallback && !loginAttempted && !hasLoginRequiredError && !isPostLogout) {
          initOptions.onLoad = 'check-sso';
          initOptions.silentCheckSsoRedirectUri = origin + '/silent-check-sso.html';
        }

        // Add timeout to prevent hanging on init (especially in dev environments)
        const initTimeout = 10000; // 10 seconds
        const initPromise = keycloakInstance.init(initOptions);
        const timeoutPromise = new Promise<boolean>((_, reject) => {
          setTimeout(() => reject(new Error('Keycloak init timeout')), initTimeout);
        });

        const auth = await Promise.race([initPromise, timeoutPromise]).catch((error) => {
          console.warn('Keycloak init error (non-critical):', error);
          // Return false to indicate not authenticated
          return false;
        });

        console.log('Keycloak init result:', { 
          auth, 
          authenticated: keycloakInstance.authenticated,
          token: !!keycloakInstance.token,
          tokenParsed: !!keycloakInstance.tokenParsed
        });

        setKeycloak(keycloakInstance);
        // Use keycloakInstance.authenticated as fallback - it's more reliable
        const isAuthenticated = auth === true || keycloakInstance.authenticated === true;
        setAuthenticated(isAuthenticated);

        if (isAuthenticated) {
          await loadUserProfile(keycloakInstance);
          // Clear all login tracking flags on successful authentication
          sessionStorage.removeItem('keycloak_login_required');
          sessionStorage.removeItem('keycloak_login_attempted');
        } else if (loginAttempted) {
          // We tried to login but came back unauthenticated - prevent infinite loop
          console.log('Login was attempted but user is still not authenticated - showing login prompt');
          sessionStorage.setItem('keycloak_login_required', 'true');
          sessionStorage.removeItem('keycloak_login_attempted');
        }
        
        // Clean up authentication-related URL parameters after init
        // Preserve application query parameters (like workflowId)
        const cleanupUrl = new URL(window.location.href);
        const params = new URLSearchParams(cleanupUrl.search);
        
        // Remove only Keycloak auth parameters
        const keycloakParams = ['state', 'session_state', 'code', 'error', 'error_description'];
        let hasKeycloakParams = false;
        keycloakParams.forEach(param => {
          if (params.has(param)) {
            params.delete(param);
            hasKeycloakParams = true;
          }
        });
        
        // Restore any saved app params from before login/SSO redirect
        const savedParams = sessionStorage.getItem('keycloak_redirect_params');
        if (savedParams) {
          // Only restore and clear if authenticated, otherwise keep for next attempt
          if (isAuthenticated) {
            sessionStorage.removeItem('keycloak_redirect_params');
            // Merge saved params with current params (saved params take precedence)
            const savedParamsObj = new URLSearchParams(savedParams);
            savedParamsObj.forEach((value, key) => {
              if (!params.has(key)) {
                params.set(key, value);
              }
            });
            hasKeycloakParams = true; // Force URL update
            console.log('Restored app params after auth:', params.toString());
          }
        }
        
        // Only update URL if we actually removed Keycloak params or restored app params
        if (hasKeycloakParams || window.location.hash) {
          const cleanUrl = window.location.origin + window.location.pathname + 
            (params.toString() ? `?${params.toString()}` : '');
          window.history.replaceState({}, document.title, cleanUrl);
        }

        // Setup token refresh
        keycloakInstance.onTokenExpired = () => {
          keycloakInstance
            .updateToken(30)
            .then((refreshed) => {
              if (refreshed) {
                console.log('Token refreshed');
              }
            })
            .catch(() => {
              console.error('Failed to refresh token');
              keycloakInstance.login();
            });
        };

        setLoading(false);
      } catch (error) {
        console.error('Failed to initialize Keycloak', error);
        initializingRef.current = false;
        keycloakRef.current = null;
        setLoading(false);
      }
    };

    initKeycloak();
    
    // Cleanup function for StrictMode
    return () => {
      // Don't cleanup keycloak instance - we want to preserve it
    };
  }, [keycloakConfig.url, keycloakConfig.realm, keycloakConfig.clientId]);

  const loadUserProfile = async (keycloakInstance: Keycloak) => {
    try {
      const profile = await keycloakInstance.loadUserProfile();
      const tokenParsed = keycloakInstance.tokenParsed;
      
      // Try to get email from profile first, then fallback to tokenParsed
      const email = profile.email || tokenParsed?.email;
      
      setUser({
        username: profile.username || tokenParsed?.preferred_username,
        email: email,
        firstName: profile.firstName || tokenParsed?.given_name,
        lastName: profile.lastName || tokenParsed?.family_name,
        roles: tokenParsed?.realm_access?.roles || [],
      });
    } catch (error) {
      console.error('Failed to load user profile', error);
      // Fallback to token parsed data if profile loading fails
      const tokenParsed = keycloakInstance.tokenParsed;
      if (tokenParsed) {
        setUser({
          username: tokenParsed.preferred_username,
          email: tokenParsed.email,
          firstName: tokenParsed.given_name,
          lastName: tokenParsed.family_name,
          roles: tokenParsed.realm_access?.roles || [],
        });
      }
    }
  };

  const login = () => {
    if (keycloak) {
      // Mark that we're attempting login - used to detect failed login loops
      sessionStorage.setItem('keycloak_login_attempted', 'true');
      
      // Preserve query parameters (like workflowId) when redirecting to Keycloak
      // Only save if not already saved (might have been saved during init)
      if (!sessionStorage.getItem('keycloak_redirect_params')) {
        const currentUrl = new URL(window.location.href);
        const params = new URLSearchParams(currentUrl.search);
        
        // Remove Keycloak auth params that shouldn't be preserved
        const keycloakParams = ['state', 'session_state', 'code', 'error', 'error_description'];
        keycloakParams.forEach(param => params.delete(param));
        
        // Store remaining app params (like workflowId) in sessionStorage
        const appParams = params.toString();
        if (appParams) {
          console.log('Saving app params before login redirect:', appParams);
          sessionStorage.setItem('keycloak_redirect_params', appParams);
        }
      }
      
      // Use the base URL without query params for Keycloak redirect
      // The app params will be restored after successful authentication
      const redirectUri = window.location.origin + window.location.pathname;
      keycloak.login({
        redirectUri: redirectUri,
      });
    }
  };

  const logout = () => {
    if (keycloak) {
      // Clear all stored workspace preferences so the default loads fresh on next login
      Object.keys(localStorage)
        .filter(key => key.startsWith('enveu-current-workspace-'))
        .forEach(key => localStorage.removeItem(key));

      // Flag so the next init skips check-sso — the user is definitely unauthenticated
      // after logout, and running check-sso triggers a 3p-cookie iframe check that hangs
      // for up to 10 seconds when the iframe is blocked by the browser.
      sessionStorage.setItem('keycloak_post_logout', 'true');

      keycloak.logout({
        redirectUri: window.location.origin,
      });
    }
  };

  const hasRole = (role: string): boolean => {
    return keycloak?.hasRealmRole(role) || false;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => hasRole(role));
  };

  const value: AuthContextType = {
    keycloak,
    authenticated,
    loading,
    token: keycloak?.token,
    user,
    login,
    logout,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
